import React from 'react';
import {
  SessionRecord,
  formatSessionAmount,
  formatSessionDate,
  formatSessionTime,
  getSessionStatusColor,
  getSessionTypeName,
} from '@theraply/api-client';

interface SessionCardProps {
  session: SessionRecord;
  userType: 'CONSUMER' | 'THERAPIST';
  onViewDetails: (sessionId: number) => void;
  onDownloadReceipt?: (sessionId: number) => void;
}

export const SessionCard: React.FC<SessionCardProps> = ({
  session,
  userType,
  onViewDetails,
  onDownloadReceipt,
}) => {
  const statusColor = getSessionStatusColor(session.status);
  const sessionTypeName = getSessionTypeName(session.sessionType);

  // Determine the display name based on user type
  const participantName =
    userType === 'CONSUMER' ? session.therapistName : session.consumerName;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-200 border border-gray-200">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {participantName || 'Unknown'}
          </h3>
          <p className="text-sm text-gray-500">{sessionTypeName}</p>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium bg-${statusColor}-100 text-${statusColor}-800`}
        >
          {session.status.replace('_', ' ')}
        </span>
      </div>

      {/* Date and Time */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-gray-500 mb-1">Date</p>
          <p className="text-sm font-medium text-gray-900">
            {formatSessionDate(session.sessionDate)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Time</p>
          <p className="text-sm font-medium text-gray-900">
            {formatSessionTime(session.startTime)} -{' '}
            {formatSessionTime(session.endTime)}
          </p>
        </div>
      </div>

      {/* Duration and Amount */}
      <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-200">
        <div>
          <p className="text-xs text-gray-500 mb-1">Duration</p>
          <p className="text-sm font-medium text-gray-900">
            {session.durationMinutes} minutes
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">
            {userType === 'CONSUMER' ? 'Amount Paid' : 'Earnings'}
          </p>
          <p className="text-sm font-medium text-gray-900">
            {formatSessionAmount(
              userType === 'CONSUMER'
                ? session.amountPaid
                : session.therapistEarnings
            )}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onViewDetails(session.id)}
          className="flex-1 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
        >
          View Details
        </button>
        {session.hasReceipt && onDownloadReceipt && (
          <button
            onClick={() => onDownloadReceipt(session.id)}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Download Receipt
          </button>
        )}
      </div>
    </div>
  );
};
